import PropTypes from 'prop-types';
import { useState, useRef } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Link, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';

// ----------------------------------------------------------------------

FileUploaderWithPreview.propTypes = {
  name: PropTypes.string.isRequired,
  accept: PropTypes.string,
  currentUrl: PropTypes.string,
};

FileUploaderWithPreview.defaultProps = {
  accept: '*',
  currentUrl: '',
};

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function FileUploaderWithPreview({ name, accept, currentUrl }) {
  const { control } = useFormContext();
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const handleFile = (file) => {
          setSelectedFile(file);
          field.onChange(file);
        };

        const handleDrop = (e) => {
          e.preventDefault();
          setIsDragging(false);
          const file = e.dataTransfer.files?.[0];
          if (file) handleFile(file);
        };

        const handleClear = (e) => {
          e.stopPropagation();
          setSelectedFile(null);
          field.onChange(null);
        };

        const hasContent = !!selectedFile || !!currentUrl;

        let borderColor = 'divider';
        if (error) borderColor = 'error.main';
        else if (isDragging) borderColor = 'primary.main';
        else if (selectedFile) borderColor = 'success.light';
        else if (currentUrl) borderColor = 'success.light';

        let bgColor = '#f5f7ff';
        if (isDragging) bgColor = 'rgba(25, 118, 210, 0.08)';
        else if (hasContent) bgColor = 'rgba(46, 125, 50, 0.04)';

        const iconColor = isDragging ? 'primary.main' : 'text.disabled';
        const labelColor = isDragging ? 'primary.main' : 'text.secondary';
        const dropLabel = isDragging ? t('drop_here') : t('drag_or_click_to_upload');

        let fileDisplay;
        if (selectedFile) {
          fileDisplay = (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
              <InsertDriveFileIcon sx={{ fontSize: 38, color: 'primary.main', flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} noWrap>{selectedFile.name}</Typography>
                <Typography variant="caption" color="text.secondary">{formatBytes(selectedFile.size)}</Typography>
              </Box>
              <IconButton size="small" onClick={handleClear}
                sx={{ flexShrink: 0, color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        } else if (currentUrl) {
          const currentFileName = currentUrl.split('/').pop().split('?')[0] || t('current_document');
          fileDisplay = (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
              <InsertDriveFileIcon sx={{ fontSize: 38, color: 'success.main', flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} noWrap>{currentFileName}</Typography>
                <Link
                  href={currentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  underline="hover"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('view_document') || 'Ver documento'}
                </Link>
              </Box>
              <IconButton size="small"
                onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                sx={{ flexShrink: 0, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                title={t('change_file') || 'Cambiar archivo'}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        } else {
          fileDisplay = (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, userSelect: 'none', pointerEvents: 'none' }}>
              <UploadFileIcon sx={{ fontSize: 44, color: iconColor, transition: 'color 0.2s' }} />
              <Typography variant="body2" color={labelColor} sx={{ transition: 'color 0.2s' }}>{dropLabel}</Typography>
            </Box>
          );
        }

        return (
          <Box>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                borderRadius: 2,
                border: '2px dashed',
                borderColor,
                background: bgColor,
                cursor: hasContent ? 'default' : 'pointer',
                transition: 'border-color 0.2s, background 0.2s',
                '&:hover': { borderColor: hasContent ? borderColor : 'primary.main' },
                p: 2.5,
                minHeight: 90,
                display: 'flex',
                alignItems: 'center',
                justifyContent: hasContent ? 'flex-start' : 'center',
              }}
              onClick={() => { if (!hasContent) inputRef.current?.click(); }}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onDrop={handleDrop}
            >
              {fileDisplay}

              <input
                ref={inputRef}
                type="file"
                accept={accept}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                  e.target.value = '';
                }}
                hidden
              />
            </Box>

            {error && (
              <Typography variant="caption" color="error.main" display="block" mt={0.5}>
                {error.message}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
}
